import { useState, useEffect, useCallback } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents, Circle, useMap } from 'react-leaflet'
import { MapPin, Navigation } from 'lucide-react'
import L from 'leaflet'
import { t } from '../../i18n/translations'

// Fix Leaflet default marker icon
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import shadowUrl from 'leaflet/dist/images/marker-shadow.png'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl })

interface MapPointPickerProps {
  lat?: number | null
  lng?: number | null
  radiusKm?: number | null
  onChange: (lat: number, lng: number, radiusKm: number) => void
  onClear?: () => void
  className?: string
}

// Default center: Moscow
const DEFAULT_CENTER: [number, number] = [55.7558, 37.6173]
const DEFAULT_ZOOM = 11
const RADIUS_OPTIONS = [0.5, 1, 2, 5, 10, 20, 50]

// Sub-component: handles map clicks
function MapClickHandler({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng)
    },
  })
  return null
}

// Sub-component: fly to position when coordinates change externally
function MapFlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap()
  useEffect(() => {
    map.flyTo([lat, lng], map.getZoom(), { duration: 0.8 })
  }, [lat, lng, map])
  return null
}

export default function MapPointPicker({
  lat,
  lng,
  radiusKm,
  onChange,
  onClear,
  className = ''
}: MapPointPickerProps) {
  const [position, setPosition] = useState<[number, number] | null>(
    lat && lng ? [lat, lng] : null
  )
  const [radius, setRadius] = useState<number>(radiusKm || 5)
  const [detecting, setDetecting] = useState(false)
  const [geoError, setGeoError] = useState(false)

  useEffect(() => {
    if (lat && lng) {
      setPosition([lat, lng])
    }
  }, [lat, lng])

  useEffect(() => {
    if (radiusKm != null) {
      setRadius(radiusKm)
    }
  }, [radiusKm])

  const handleMapClick = useCallback((newLat: number, newLng: number) => {
    setPosition([newLat, newLng])
    onChange(newLat, newLng, radius)
  }, [radius, onChange])

  const handleRadiusChange = useCallback((newRadius: number) => {
    setRadius(newRadius)
    if (position) {
      onChange(position[0], position[1], newRadius)
    }
  }, [position, onChange])

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      setGeoError(true)
      return
    }
    setGeoError(false)
    setDetecting(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        setPosition([latitude, longitude])
        onChange(latitude, longitude, radius)
        setDetecting(false)
      },
      () => {
        setGeoError(true)
        setDetecting(false)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const handleClearPin = () => {
    setPosition(null)
    setGeoError(false)
    onClear?.()
  }

  const center: [number, number] = position || DEFAULT_CENTER

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Map */}
      <div className="relative isolate z-0 rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100"
        style={{ height: '220px', touchAction: 'none' }}>
        <MapContainer
          center={center}
          zoom={DEFAULT_ZOOM}
          className="w-full h-full"
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <MapClickHandler onClick={handleMapClick} />
          {position && <MapFlyTo lat={position[0]} lng={position[1]} />}
          {position && (
            <>
              <Marker position={position} />
              <Circle
                center={position}
                radius={radius * 1000}
                pathOptions={{
                  fillColor: '#3b82f6',
                  fillOpacity: 0.12,
                  color: '#3b82f6',
                  weight: 1.5,
                }}
              />
            </>
          )}
        </MapContainer>

        {/* Detect location button */}
        <button
          onClick={handleDetectLocation}
          disabled={detecting}
          className="absolute top-2 right-2 z-[1000] w-9 h-9 bg-white rounded-xl shadow-md
            flex items-center justify-center hover:bg-gray-50 transition-colors
            active:scale-90 disabled:opacity-50"
          title={t('location.useMyLocation')}
        >
          <Navigation className={`w-4 h-4 text-blue-500 ${detecting ? 'animate-spin' : ''}`} />
        </button>

        {/* Tap hint */}
        {!position && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg
              text-sm text-gray-600 font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-500" />
              {t('location.tapToSet')}
            </div>
          </div>
        )}
      </div>

      {/* Geolocation error feedback */}
      {geoError && (
        <p className="text-xs text-red-500 -mt-1">{t('location.geoError')}</p>
      )}

      {/* Radius selector */}
      {position && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('location.approxArea')} · {t('location.withinRadius').replace('{radius}', String(radius))}
            </label>
            <button
              onClick={handleClearPin}
              className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
            >
              {t('location.clearPoint')}
            </button>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {RADIUS_OPTIONS.map((r) => (
              <button
                key={r}
                onClick={() => handleRadiusChange(r)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 ${
                  radius === r
                    ? 'bg-blue-500 text-white shadow-md shadow-blue-500/20'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {r < 1 ? `${r * 1000}m` : `${r}km`}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">
            {t('location.preciseHint')}
          </p>
        </div>
      )}
    </div>
  )
}
